/**
 * How a PROGRAMME is described in words — the sentences and figures a "programme details"
 * surface prints.
 *
 * Sits in the same layer as `./program`: arguments in, answers out, no store, no platform API.
 * `program.ts` answers what the programme PERMITS (`getBurnRules`, `canShowLoyaltyStanding`);
 * this file answers how to SAY it ("Weekdays, 9:00 AM - 11:00 AM", "once every 24 hours").
 *
 * 🔴 **Why these are rules and not formatting.** Three surfaces render the same programme —
 * the POS's customer-summary Loyalty tab, "View program details" on the consent block, and
 * "Know more" on an ineligible bill — and until this file existed each read the raw programme
 * and phrased it itself. `getDayText` is the clearest case: `[1,2,3,4,5]` is "Weekdays", and a
 * surface that maps day numbers to names inline prints "Monday & Tuesday & Wednesday &
 * Thursday & Friday". That is a rule with a right answer, so it is tested
 * (`__tests__/programDisplay.test.ts`), not re-decided per view.
 *
 * ⚠️ **`collectibleLabel` is passed IN, never resolved here.** It comes from
 * `useCollectibleLabel`, which reads the store. Taking it as an argument is what keeps this
 * module pure and testable with a stub.
 *
 * Uplifted 2026-09-08 from (now-deleted) `apps/web/src/Containers/LoyaltyLeg/PointsBaseRewardScreen/
 * Components/helper.tsx` (the pure half — its `renderCollectibleIcon` is a view and went to
 * `@rista/ui`) plus `pluralize` from that folder's `LoyaltyHelper.tsx`.
 */
import {
  BONUS_LEDGER_TYPE,
  BONUS_TYPES,
  COOLOFF_TYPES,
  HISTORY_TYPES,
  MULTIPLIER_RULE_TYPES,
  WELCOME_BONUS_REASON,
} from "./constants";

/** `Date.getDay()` order — index 0 is Sunday, matching what the programme sends. */
export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const WEEKDAY_INDICES = [1, 2, 3, 4, 5];

/**
 * A points figure as a person should READ it.
 *
 * 🔴 **Nothing guarantees a whole number.** A balance is `currentPoints` passed straight through
 * from the provider, and an applied figure comes off the discount on the sale — so
 * "242.4 Points" is reachable on every surface, and reads as a broken screen rather than as
 * precision.
 *
 * ⚠️ **It ROUNDS where a ceiling FLOORS, and that asymmetry is the point.** A ceiling bounds
 * something: round it up and `applyMax` sends points the provider never offered, which is why
 * `getRedemptionLimits` floors `maxPoints` at source, for every reader. A balance bounds
 * nothing, so it takes the nearest whole number — what someone reading "you have 243" expects
 * of 242.6. The one visible consequence is that a balance can print one above a ceiling derived
 * from the same fraction ("Use 242 of 243"), which reads correctly anyway: a ceiling below the
 * balance is the ordinary case.
 *
 * By the time `limits.maxPoints` reaches here it is already whole, so this is identity on it.
 * `NaN` reads as 0 rather than printing "NaN Points".
 *
 * Shared by the POS panel (`loyalty/panelModel`) and the kiosk card
 * (`kiosk/loyalty/pointsCardModel`), which each held their own copy of this rule until
 * 2026-09-09. Display-only: this value is never sent anywhere.
 */
export const displayPoints = (value: number): number => Math.round(value) || 0;

/**
 * Singular or plural form of `word` for `count`.
 *
 * ⚠️ Zero is PLURAL ("0 days"), which is English rather than an oversight — only exactly 1 takes
 * the singular. A word already ending in `s` is left alone so "points" does not become
 * "pointss"; pass `customPlural` for anything irregular.
 */
export function pluralize(count: number, word: string, customPlural?: string): string {
  if (count === 1) return word;
  if (customPlural) return customPlural;
  if (word.endsWith("s")) return word;
  return `${word}s`;
}

/**
 * A programme date, as `MM/DD/YYYY` or (with `isMonthFormat`) `Mon D, YYYY`.
 *
 * Answers `""` for a missing or unparseable value rather than "Invalid Date" — every caller
 * renders this straight into a sentence, and `new Date(undefined)` stringifies to exactly that.
 */
export function formatProgramDate(dateStr?: string | null, isMonthFormat = false): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return isMonthFormat
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

/** `"14:30"` -> `"2:30 PM"`. Answers `""` for anything it cannot read. */
export function formatTime12(time24?: string | null): string {
  if (!time24) return "";
  const [h, m] = String(time24).split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

/**
 * How often points may be earned, as the tail of "You can earn points ___".
 *
 * The four special cases are not cosmetic: `hours: 0` means no cool-off at all, and printing
 * "once every 0 hours" reads as "never" — the opposite of what it configures.
 */
export function getCooloffText(type?: string | null, value?: unknown): string {
  const v = Number(value);
  if (!type || Number.isNaN(v)) return "";
  if (type === COOLOFF_TYPES.HOURS) {
    if (v === 0) return "on every qualifying order";
    if (v === 24) return "once every 24 hours";
    return `once every ${v} hours`;
  }
  if (type === COOLOFF_TYPES.CALENDAR_DAY) {
    if (v === 1) return "once per calendar day";
    return `once every ${v} calendar days`;
  }
  return "";
}

/**
 * Which days a boost runs, collapsed to the phrase a human would use.
 *
 * ⚠️ The weekday test is `contains all of Mon–Fri`, NOT `length === 5`: a programme that runs
 * Mon–Fri plus Saturday still reads better as a list than as "Weekdays", so the `length >= 5`
 * guard keeps the collapse to sets that genuinely cover the working week. Checked before
 * `length === 7` would matter because Everyday contains the weekdays too — hence Everyday is
 * tested first below.
 */
export function getDayText(days?: number[] | null): string {
  if (!days?.length) return "";
  if (days.length === 7) return "Everyday";
  if (days.length === 2 && days.includes(0) && days.includes(6)) return "Weekends";
  if (days.length >= 5 && WEEKDAY_INDICES.every((d) => days.includes(d))) return "Weekdays";
  return days.map((d) => DAY_NAMES[d] ?? String(d)).join(" & ");
}

export interface MultiplierDisplay {
  /** "Earn 2x Points" */
  headline: string;
  /** When it applies — days and times, or the categories it covers. `""` when unconditional. */
  schedule: string;
  /**
   * Points per 100 units of currency, WITH the boost. The view prints the merchant's own symbol
   * beside it (`BoostsBonuses` does `per ${money(100, { decimals: 0 })} spent`) — this number
   * carries none.
   */
  boosted: number;
  /** Points per ₹100 without it. */
  base: number;
}

/**
 * One multiplier, ready to render.
 *
 * `earnPerPoints` falls back to 1 so `boosted`/`base` are always a real comparison — a
 * programme that has not published an earn rate would otherwise show "0 Points (0 Points)".
 */
export function getMultiplierDisplay(
  multiplier: any,
  earnPerPoints: number | undefined,
  collectibleLabel: (count?: number, transform?: string) => string,
): MultiplierDisplay {
  const factor = Number(multiplier?.multiplier) || 1;
  const base = Number(earnPerPoints) || 1;
  const headline = `Earn ${factor}x ${collectibleLabel(factor)}`;
  const display: MultiplierDisplay = { headline, schedule: "", boosted: base * factor, base };

  if (multiplier?.ruleType === MULTIPLIER_RULE_TYPES.DAY_TIME) {
    const slot = multiplier?.config?.time_slots?.[0];
    const timeText = slot ? `${formatTime12(slot.from)} - ${formatTime12(slot.to)}` : "";
    const dayText = getDayText(multiplier?.config?.days);
    display.schedule = [dayText, timeText].filter(Boolean).join(", ");
    return display;
  }

  if (multiplier?.ruleType === MULTIPLIER_RULE_TYPES.CATEGORY_ITEM) {
    display.schedule = Array.isArray(multiplier?.config)
      ? multiplier.config
          .map((c: any) => c?.name)
          .filter(Boolean)
          .join(", ")
      : "selected categories";
    return display;
  }

  return display;
}

export interface BonusDisplay {
  headline: string;
  description: string;
}

/** One bonus, ready to render. A welcome bonus says so; everything else is a plain bonus. */
export function getBonusDisplay(
  bonus: any,
  collectibleLabel: (count?: number, transform?: string) => string,
): BonusDisplay {
  const value = Number(bonus?.value) || 0;
  return {
    headline: `Earn +${value} ${collectibleLabel(value)}`,
    description:
      bonus?.bonusType
        ? `Get ${value} ${collectibleLabel(value)} as a ${bonus?.name}`
        : `Get ${value} bonus ${collectibleLabel(value)}`,
  };
}

/**
 * Whether the customer has already collected the welcome bonus.
 *
 * ⚠️ Both halves are load-bearing: the customer's ledger carries every kind of entry, so
 * `reason === "welcome_bonus"` alone would match a `burn` row that referenced it. The web
 * original tested `type === "bonus"` too — kept, not tidied away.
 */
export function isWelcomeBonusEarned(customerLoyaltyInfo: any): boolean {
  return !!customerLoyaltyInfo?.bonus?.some(
    (b: any) => b?.type === BONUS_LEDGER_TYPE && b?.reason === WELCOME_BONUS_REASON,
  );
}

/** How one row of the customer's points ledger is drawn. */
export interface HistoryItemModel {
  /** What happened, in the programme's own words. */
  title: string;
  /** `"+40"` / `"-40"` — signed, so the view never decides the sign. */
  signedPoints: string;
  /** The collectible label for the magnitude. */
  pointsLabel: string;
  /** Green for earned, muted red for spent or expired. */
  tone: "earned" | "deducted";
  /** `"Earned on 03/09/2026"`, or `""` when the row carries no date. */
  earnedOnLabel: string;
  /** `"Expired on 03/09/2026"` — only for an expiry row that has the date. */
  expiredOnLabel: string;
  /** The order total this row came from, or `undefined` when it is not order-linked. */
  orderTotal?: number;
  /** Expiry rows are shaded. */
  isExpired: boolean;
}

/**
 * One ledger row, ready to render.
 *
 * ⚠️ **`type` decides the sign, and there are two ways to lose points.** `burn` is a redemption
 * and `expire` is a lapse; both subtract, and only `expire` is shaded and carries an expiry
 * date. The web original computed all of this inline in `HistoryItem` and, notably, printed
 * `Earned on <date>` for EVERY row including a burn — preserved here as `earnedOnLabel` rather
 * than silently corrected, because the label is what the ledger's `createdAt` means for the
 * common case and changing it is a copy decision, not a refactor. It is a separate field from
 * `expiredOnLabel` so a view can phrase them differently without re-deriving the type.
 */
export function getHistoryItemModel(
  item: any,
  collectibleLabel: (count?: number, transform?: string) => string,
): HistoryItemModel {
  const isExpired = item?.type === HISTORY_TYPES.EXPIRE;
  const isDeducted = item?.type === HISTORY_TYPES.BURN;
  const points = Math.abs(Number(item?.points) || 0);
  const fallbackTitle = isExpired
    ? `${collectibleLabel()} expired`
    : isDeducted
      ? `${collectibleLabel()} redeemed`
      : `${collectibleLabel()} earned`;
  const earnedOn = formatProgramDate(item?.createdAt);
  const expiredOn = formatProgramDate(item?.expiresAt);

  return {
    title: item?.title || item?.description || fallbackTitle,
    signedPoints: `${isExpired || isDeducted ? "-" : "+"}${points}`,
    pointsLabel: collectibleLabel(points),
    tone: isExpired || isDeducted ? "deducted" : "earned",
    earnedOnLabel: earnedOn ? `Earned on ${earnedOn}` : "",
    expiredOnLabel: isExpired && expiredOn ? `Expired on ${expiredOn}` : "",
    orderTotal: item?.amount == null ? undefined : Number(item.amount) || 0,
    isExpired,
  };
}
